import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api } from '@/api';
import { Spinner } from '@/components/Spinner';

const schema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your password'),
  })
  .superRefine((val, ctx) => {
    if (val.password !== val.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: 'Passwords do not match' });
    }
  });
type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  // 'checking' → verifying the token; 'valid' → show form; 'invalid' → dead link; 'done' → success.
  const [phase, setPhase] = useState<'checking' | 'valid' | 'invalid' | 'done'>('checking');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    let active = true;
    if (!token) {
      setPhase('invalid');
      return;
    }
    api.resetCheck(token).then((valid) => {
      if (active) setPhase(valid ? 'valid' : 'invalid');
    });
    return () => {
      active = false;
    };
  }, [token]);

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    const res = await api.resetPassword(token, values.password, values.confirmPassword);
    if (res.err === '1' || res.err === 1) {
      setPhase('done');
    } else if (res.err === '-4') {
      setPhase('invalid');
    } else {
      setServerError(typeof res.message === 'string' ? res.message : 'Could not reset your password.');
    }
  };

  return (
    <AuthLayout>
      {phase === 'checking' && (
        <div className="text-center py-4"><Spinner /></div>
      )}

      {phase === 'invalid' && (
        <div className="ast-recover sans-serif text-center">
          This reset link is invalid or has expired.
          <br />
          <Link to="/login" className="ast-link">Return to sign in</Link> to request a new one.
        </div>
      )}

      {phase === 'done' && (
        <div className="ast-recover sans-serif text-center">
          Your password has been reset.
          <br />
          <Link to="/login" className="ast-link">Sign in</Link> with your new password.
        </div>
      )}

      {phase === 'valid' && (
        <form className="ast-login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="form-floating">
            <input
              className="form-control sans-serif ast-input-top"
              id="reset-password"
              type="password"
              placeholder="New Password"
              autoComplete="new-password"
              {...register('password')}
            />
            <label htmlFor="reset-password">New Password</label>
          </div>
          <div className="form-floating">
            <input
              className="form-control sans-serif ast-input-bottom"
              id="reset-confirm"
              type="password"
              placeholder="Confirm Password"
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
            <label htmlFor="reset-confirm">Confirm Password</label>
          </div>

          <div className="ast-login-error sans-serif">
            {errors.password?.message || errors.confirmPassword?.message || serverError || ' '}
          </div>

          <button
            type="submit"
            className="btn btn-ast-blue function-button sans-serif w-100 mb-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Reset Password'}
          </button>
          <div className="text-center">
            <button type="button" className="ast-link" onClick={() => navigate('/login')}>
              Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
